import { NgSelectModule } from '@ng-select/ng-select';
import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import {
  IBoton,
  ICentroMonitoreo,
  IConfigVecino,
  ICrearAlertaMonitoreo,
} from 'modelosSeguridad/src';
import { IFilter, IGeoJSONPoint, IQueryParam } from 'modelos/src';
import { MaterialModule } from '../../../../auxiliares/material.module';
import { CardHeaderComponent } from '../../../../componentes/card-header/card-header.component';
import { ContentCardComponent } from '../../../../componentes/content-card/content-card.component';
import { LoadingComponent } from '../../../../componentes/loading/loading.component';
import { ButtonComponent } from '../../../../auxiliares/button/button.component';
import { MapaUbicarComponent } from '../../../../auxiliares/mapas/mapa-ubicar/mapa-ubicar.component';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { IntegracionesService } from '../../../../auxiliares/servicios/http/integraciones.service';
import { LocalidadSelectorComponent } from '../../../../componentes/selectores/localidad-selector/localidad-selector.component';

@Component({
  selector: 'app-crear-alerta-seguridad',
  templateUrl: './crear-alerta-seguridad.component.html',
  styleUrl: './crear-alerta-seguridad.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    CardHeaderComponent,
    ContentCardComponent,
    LoadingComponent,
    ButtonComponent,
    MapaUbicarComponent,
    LocalidadSelectorComponent,
    NgSelectModule,
  ],
})
export class CrearAlertaSeguridadComponent implements OnInit {
  dialogRef = inject<MatDialogRef<CrearAlertaSeguridadComponent>>(
    MatDialogRef,
    { optional: true },
  );
  helper = inject(HelperService);
  private service = inject(IntegracionesService);

  public loading = false;
  public form?: FormGroup;
  public title = 'Crear Alerta';

  public botones: IBoton[] = [];
  public centrosMonitoreo: ICentroMonitoreo[] = [];
  // Opciones fijas del selector de derivación (sin derivar = campo vacío)
  public readonly tiposDelegacion = [
    { valor: 'normal', etiqueta: 'Normal (la alerta pasa al otro centro)' },
    {
      valor: 'visibilidad-compartida',
      etiqueta: 'Compartida (ambos centros la ven)',
    },
    { valor: 'externa', etiqueta: 'Externa (entidad fuera del sistema)' },
  ];
  public entidadesExternas: string[] = [];
  public entidadesExternasFiltradas: string[] = [];

  public buscandoVecino = false;
  public configVecino?: IConfigVecino;
  public centrarA?: IGeoJSONPoint;

  public telefonoPattern = '^[1-9][0-9]{9}$';
  private telefonoRegex?: RegExp;

  get geojson(): FormGroup {
    return this.form?.get('ubicacion') as FormGroup;
  }

  get vecinoForm(): FormGroup {
    return this.form?.get('vecino') as FormGroup;
  }

  get tipoDelegacion(): string | null {
    return this.form?.get('tipoDelegacion')?.value ?? null;
  }

  private createForm(): void {
    this.form = new FormGroup({
      direccion: new FormControl(null, Validators.required),
      detallesDireccion: new FormControl(null),
      ubicacion: new FormGroup({
        type: new FormControl('Point'),
        coordinates: new FormControl(null),
      }),
      idConfigVecino: new FormControl(null),
      idBoton: new FormControl(null, Validators.required),
      idLocalidad: new FormControl(null),
      tipoDelegacion: new FormControl(null),
      idCentroDerivado: new FormControl(null),
      entidadExterna: new FormControl(null),
      vecino: new FormGroup({
        nombre: new FormControl(null),
        dni: new FormControl(null),
        telefono: new FormControl(null, [
          ...(this.telefonoPattern
            ? [Validators.pattern(this.telefonoPattern)]
            : []),
        ]),
      }),
    });

    this.form.get('tipoDelegacion')?.valueChanges.subscribe((tipo) => {
      const centro = this.form?.get('idCentroDerivado');
      const externa = this.form?.get('entidadExterna');
      centro?.clearValidators();
      externa?.clearValidators();
      if (tipo === 'externa') {
        externa?.setValidators([Validators.required]);
        if (!this.entidadesExternas.length) this.cargarEntidadesExternas();
      } else if (tipo) {
        centro?.setValidators([Validators.required]);
      }
      centro?.updateValueAndValidity();
      externa?.updateValueAndValidity();
    });

    this.form.get('entidadExterna')?.valueChanges.subscribe((valor) => {
      this.filtrarEntidadesExternas(valor || '');
    });
  }

  public volver(res?: any): void {
    if (this.dialogRef) this.dialogRef.close(res);
  }

  public patchDireccion(direccion: string): void {
    this.form?.patchValue({ direccion });
  }

  public async onSubmit(): Promise<void> {
    if (!this.form?.valid) return;
    this.loading = true;
    try {
      const data = this.form.value as ICrearAlertaMonitoreo;
      data.creadoPorMonitoreo = true;
      const res = await this.service.crearAlerta(data);
      this.helper.notifSuccess('Alerta creada');
      this.volver(res);
    } catch (err) {
      console.error(err);
      this.helper.notifError(err);
    }
    this.loading = false;
  }

  public async buscarVecino(telefono: string): Promise<void> {
    if (!telefono?.length) {
      this.configVecino = undefined;
      this.centrarA = undefined;
      this.form?.patchValue({
        idConfigVecino: null,
        vecino: { nombre: null, dni: null },
      });
      this.vecinoForm?.get('nombre')?.enable();
      this.vecinoForm?.get('dni')?.enable();
      return;
    }

    if (this.telefonoRegex && !this.telefonoRegex.test(telefono)) return;

    this.buscandoVecino = true;
    try {
      const filter: IFilter<IConfigVecino> = {
        'datosPersonales.telefono': telefono,
      } as any;
      const query: IQueryParam = { filter: JSON.stringify(filter), limit: 1 };
      const res = await this.service.getConfigVecinosFiltered(query);
      this.configVecino = res.datos?.[0];
      if (this.configVecino) {
        this.form?.patchValue({
          idConfigVecino: this.configVecino._id,
          vecino: {
            nombre: this.configVecino.datosPersonales?.nombre,
            dni: this.configVecino.datosPersonales?.dni,
          },
        });
        this.vecinoForm?.get('nombre')?.disable();
        this.vecinoForm?.get('dni')?.disable();
      } else {
        this.form?.patchValue({ idConfigVecino: null });
        this.vecinoForm?.get('nombre')?.enable();
        this.vecinoForm?.get('dni')?.enable();
      }
    } catch (err) {
      console.error('Error buscando vecino', err);
    }
    this.buscandoVecino = false;
  }

  private async cargarBotones(): Promise<void> {
    try {
      const res = await this.service.getSeguridadBotones({ limit: 500 });
      this.botones = res?.datos || [];
    } catch (err) {
      console.error('Error cargando botones', err);
    }
  }

  private async cargarCentrosMonitoreo(): Promise<void> {
    try {
      const res = await this.service.getCentrosMonitoreo({
        filter: JSON.stringify({ tipo: 'Derivado' }),
        limit: 500,
      });
      this.centrosMonitoreo = res?.datos || [];
    } catch (err) {
      console.error('Error cargando centros monitoreo', err);
    }
  }

  private async cargarEntidadesExternas(): Promise<void> {
    try {
      this.entidadesExternas = await this.service.getEntidadesExternas();
      this.entidadesExternas.sort((a, b) => a.localeCompare(b));
      this.entidadesExternasFiltradas = [...this.entidadesExternas];
    } catch (err) {
      console.error('Error cargando entidades externas', err);
    }
  }

  private filtrarEntidadesExternas(valor: string): void {
    if (!valor || !valor.trim()) {
      this.entidadesExternasFiltradas = [...this.entidadesExternas];
      return;
    }
    const f = valor.toLowerCase().trim();
    this.entidadesExternasFiltradas = this.entidadesExternas.filter((e) =>
      e.toLowerCase().includes(f),
    );
  }

  public displayFn(value: string): string {
    return value || '';
  }

  async ngOnInit(): Promise<void> {
    this.loading = true;
    this.telefonoRegex = new RegExp(this.telefonoPattern);
    this.createForm();
    await Promise.all([this.cargarBotones(), this.cargarCentrosMonitoreo()]);
    this.loading = false;
  }
}
